import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, X, Check, Plus } from "lucide-react";

export default function HREmail() {
  const [rows, setRows] = useState([]);
  const [simulate, setSimulate] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => setRows((await api.get("/hr-email/queue")).data);
  useEffect(() => { load(); }, []);

  const classify = async () => {
    if (!simulate.from_email || !simulate.body_text) return toast.warning("From + body required");
    await api.post("/hr-email/intake", simulate);
    toast.success("Classified"); setSimulate(null); load();
  };

  const decide = async (iid, status) => {
    await api.post(`/hr-email/${iid}/decide`, { status });
    toast.success(`Marked ${status}`); setEditing(null); load();
  };

  const syncInbox = async () => {
    try {
      const r = await api.post("/hr-email/sync");
      toast.success(`Synced · fetched ${r.data.fetched} · created ${r.data.created}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — check Settings → Google");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Mail size={20} /> Team inbox queue</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Classified by Claude. Low-confidence stays pending for human review.</p>
        </div>
        <button onClick={syncInbox} className="tt-btn-secondary flex items-center gap-2 mr-2" data-testid="hr-sync-btn"><Mail size={14} /> Sync Gmail inbox</button>
        <button onClick={() => setSimulate({ from_email: "", subject: "", body_text: "" })} className="tt-btn-primary flex items-center gap-2"><Plus size={14} /> Simulate inbound email</button>
      </div>

      <div className="tt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-xs font-mono">
            <th className="text-left px-4 py-2">From</th>
            <th className="text-left px-4 py-2">Subject</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-right px-4 py-2">Confidence</th>
            <th className="text-left px-4 py-2">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.intake_id} className="border-t border-[#1E1E1E] hover:bg-[#121212] cursor-pointer" onClick={() => setEditing(r)}>
                <td className="px-4 py-3 font-mono text-xs">{r.from_email}</td>
                <td className="px-4 py-3">{r.subject || "—"}</td>
                <td className="px-4 py-3"><span className="tt-badge-info">{r.ai_type}</span></td>
                <td className="px-4 py-3 text-right font-mono">{(r.ai_confidence * 100).toFixed(0)}%</td>
                <td className="px-4 py-3">
                  <span className={r.status === "approved" ? "tt-badge-pass" : r.status === "rejected" ? "tt-badge-danger" : "tt-badge-warn"}>{r.status}</span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#71717A]">Queue empty. Try the simulator →</td></tr>}
          </tbody>
        </table>
      </div>

      {simulate && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setSimulate(null)}>
          <div className="w-full max-w-lg tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h2 className="text-lg font-semibold">Simulate email to hr@</h2><button onClick={() => setSimulate(null)} className="tt-btn-ghost"><X size={16} /></button></div>
            <input className="tt-input mb-3" placeholder="From (employee email)" value={simulate.from_email} onChange={(e) => setSimulate({ ...simulate, from_email: e.target.value })} />
            <input className="tt-input mb-3" placeholder="Subject" value={simulate.subject} onChange={(e) => setSimulate({ ...simulate, subject: e.target.value })} />
            <textarea rows={6} className="tt-input mb-4" placeholder="Body…" value={simulate.body_text} onChange={(e) => setSimulate({ ...simulate, body_text: e.target.value })} />
            <div className="flex justify-end gap-2"><button onClick={() => setSimulate(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={classify} className="tt-btn-primary text-sm">Classify + queue</button></div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h2 className="text-lg font-semibold">{editing.subject || "(no subject)"}</h2><button onClick={() => setEditing(null)} className="tt-btn-ghost"><X size={16} /></button></div>
            <div className="text-xs font-mono text-[#71717A] mb-2">from {editing.from_email}</div>
            <div className="text-sm whitespace-pre-wrap mb-3">{editing.body_text}</div>
            <div className="tt-card p-3 mb-4">
              <div className="tt-label mb-2">Claude analysis</div>
              <div className="text-sm space-y-1">
                <div><span className="text-[#71717A]">Type:</span> {editing.ai_type}</div>
                <div><span className="text-[#71717A]">Summary:</span> {editing.ai_summary}</div>
                {editing.ai_dates?.start_date && <div><span className="text-[#71717A]">Dates:</span> <span className="font-mono">{editing.ai_dates.start_date} → {editing.ai_dates.end_date}</span></div>}
                {editing.ai_leave_type && <div><span className="text-[#71717A]">Leave type:</span> {editing.ai_leave_type}</div>}
                <div><span className="text-[#71717A]">Confidence:</span> <span className="font-mono">{(editing.ai_confidence * 100).toFixed(0)}%</span></div>
              </div>
            </div>
            {editing.status === "pending_review" && (
              <div className="flex justify-end gap-2">
                <button onClick={() => decide(editing.intake_id, "rejected")} className="tt-btn-secondary text-sm flex items-center gap-1"><X size={14} /> Reject</button>
                <button onClick={() => decide(editing.intake_id, "approved")} className="tt-btn-primary text-sm flex items-center gap-1"><Check size={14} /> Approve</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
